import { motion } from 'framer-motion'
import { colors, spacing } from '@/theme'
import { ReactNode } from 'react'

interface ButtonProps {
  children: ReactNode
  onClick?: () => void
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost'
  size?: 'sm' | 'md' | 'lg'
  disabled?: boolean
  fullWidth?: boolean
  type?: 'button' | 'submit' | 'reset'
  icon?: ReactNode
}

export function Button({
  children,
  onClick,
  variant = 'primary',
  size = 'md',
  disabled = false,
  fullWidth = false,
  type = 'button',
  icon,
}: ButtonProps) {
  const baseStyles: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing[2],
    border: 'none',
    borderRadius: '12px',
    fontWeight: 600,
    cursor: disabled ? 'not-allowed' : 'pointer',
    transition: 'all 0.2s ease',
    width: fullWidth ? '100%' : 'auto',
    opacity: disabled ? 0.6 : 1,
  }

  const sizeStyles = {
    sm: { padding: `${spacing[2]} ${spacing[4]}`, fontSize: '14px' },
    md: { padding: `${spacing[3]} ${spacing[6]}`, fontSize: '16px' },
    lg: { padding: `${spacing[4]} ${spacing[8]}`, fontSize: '18px' },
  }

  const variantStyles = {
    primary: {
      backgroundColor: colors.primary[500],
      color: colors.white,
    },
    secondary: {
      backgroundColor: colors.secondary[500],
      color: colors.white,
    },
    outline: {
      backgroundColor: 'transparent',
      color: colors.primary[500],
      border: `2px solid ${colors.primary[500]}`,
    },
    ghost: {
      backgroundColor: 'transparent',
      color: colors.primary[500],
    },
  }

  return (
    <motion.button
      type={type}
      onClick={onClick}
      disabled={disabled}
      whileHover={!disabled ? { scale: 1.02 } : {}}
      whileTap={!disabled ? { scale: 0.98 } : {}}
      style={{
        ...baseStyles,
        ...sizeStyles[size],
        ...variantStyles[variant],
      }}
    >
      {icon && <span>{icon}</span>}
      {children}
    </motion.button>
  )
}
